/**
 * VaultView - Secure storage for tokens, API keys, and credentials
 * Features: Search, create, edit, copy, version history
 */

import { vaultApi, type VaultItem, type VaultValue } from '../services/vault-api';

export class VaultView {
  private containerEl: HTMLElement | null = null;
  private items: VaultItem[] = [];
  private isLoading: boolean = false;
  private searchQuery: string = '';
  private selectedItem: VaultItem | null = null;
  private editingItem: VaultItem | null = null;
  private showHistory: { itemId: string; valueKey: string } | null = null;

  constructor() {
    // Don't get element here - DOM might not be ready
  }

  async load(): Promise<void> {
    // Get container on load, not in constructor
    this.containerEl = document.getElementById('vault-view');
    if (!this.containerEl) {
      console.error('VaultView: container #vault-view not found');
      return;
    }
    this.render();
    await this.loadData();
  }

  private async loadData(): Promise<void> {
    this.isLoading = true;
    this.renderContent();

    try {
      const result = await vaultApi.list(this.searchQuery || undefined);
      this.items = result.items;
    } catch (error) {
      console.error('Failed to load vault items:', error);
    }

    this.isLoading = false;
    this.renderContent();
  }

  private render(): void {
    if (!this.containerEl) return;

    this.containerEl.innerHTML = `
      <!-- Vault Header Bar -->
      <div class="vault-header-bar">
        <div class="vault-header-left">
          <h2 class="vault-title">Vault</h2>
          <span class="vault-count" id="vault-count">0</span>
        </div>
        <div class="vault-header-center">
          <div class="vault-search">
            <span class="material-symbols-outlined">search</span>
            <input type="text" id="vault-search" placeholder="Search vault...">
          </div>
        </div>
        <div class="vault-header-right">
          <button class="icon-btn" id="vault-add" title="Add Item">
            <span class="material-symbols-outlined">add</span>
          </button>
          <button class="icon-btn" id="vault-refresh" title="Refresh">
            <span class="material-symbols-outlined">refresh</span>
          </button>
        </div>
      </div>

      <!-- Split Container -->
      <div class="vault-split-container">
        <!-- Left: List -->
        <div class="vault-list-section">
          <div class="vault-list" id="vault-list"></div>
        </div>
        <!-- Right: Detail -->
        <div class="vault-detail-section hidden" id="vault-detail-section">
          <div class="vault-detail" id="vault-detail"></div>
        </div>
      </div>

      <!-- Modal for create/edit -->
      <div class="vault-modal hidden" id="vault-modal">
        <div class="vault-modal-content">
          <div class="vault-modal-header">
            <h2 id="vault-modal-title">Add Vault Item</h2>
            <button class="vault-modal-close" id="vault-modal-close">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
          <div class="vault-modal-body" id="vault-modal-body"></div>
          <div class="vault-modal-footer">
            <button class="btn-secondary" id="vault-modal-cancel">Cancel</button>
            <button class="btn-primary" id="vault-modal-save">Save</button>
          </div>
        </div>
      </div>
    `;

    this.attachEventListeners();
  }

  private attachEventListeners(): void {
    // Search
    const searchInput = document.getElementById('vault-search') as HTMLInputElement;
    searchInput?.addEventListener('input', (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.loadData();
    });

    // Add button
    document.getElementById('vault-add')?.addEventListener('click', () => {
      this.openCreateModal();
    });

    // Refresh button
    document.getElementById('vault-refresh')?.addEventListener('click', () => {
      this.loadData();
    });

    // Modal close
    document.getElementById('vault-modal-close')?.addEventListener('click', () => {
      this.closeModal();
    });
    document.getElementById('vault-modal-cancel')?.addEventListener('click', () => {
      this.closeModal();
    });

    // Modal save
    document.getElementById('vault-modal-save')?.addEventListener('click', () => {
      this.saveItem();
    });

    // Click outside modal to close
    document.getElementById('vault-modal')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('vault-modal')) {
        this.closeModal();
      }
    });
  }

  private renderContent(): void {
    const listEl = document.getElementById('vault-list');
    const countEl = document.getElementById('vault-count');

    console.log('[VaultView] renderContent called', {
      listEl: !!listEl,
      countEl: !!countEl,
      isLoading: this.isLoading,
      itemsCount: this.items.length
    });

    if (countEl) {
      countEl.textContent = String(this.items.length);
    }

    if (!listEl) {
      console.error('[VaultView] #vault-list not found!');
      return;
    }

    if (this.isLoading) {
      listEl.innerHTML = '<div class="vault-loading">Loading...</div>';
      return;
    }

    if (this.items.length === 0) {
      listEl.innerHTML = `
        <div class="vault-empty">
          <span class="material-symbols-outlined">lock</span>
          <p>No vault items found</p>
          <button class="btn-primary" id="vault-empty-add">Add First Item</button>
        </div>
      `;
      document.getElementById('vault-empty-add')?.addEventListener('click', () => {
        this.openCreateModal();
      });
      return;
    }

    listEl.innerHTML = this.items.map(item => `
      <div class="vault-item ${this.selectedItem?.id === item.id ? 'selected' : ''}" data-id="${item.id}">
        <div class="vault-item-header">
          <span class="vault-item-name">${this.escapeHtml(item.name)}</span>
          <span class="vault-item-count">${item.values.length} value${item.values.length !== 1 ? 's' : ''}</span>
        </div>
        <div class="vault-item-meta">
          <span class="vault-item-date">${this.formatDate(item.updatedAt)}</span>
        </div>
      </div>
    `).join('');

    // Click handlers for items
    listEl.querySelectorAll('.vault-item').forEach(el => {
      el.addEventListener('click', () => {
        const id = el.getAttribute('data-id');
        const item = this.items.find(i => i.id === id);
        if (item) {
          this.selectItem(item);
        }
      });
    });
  }

  private selectItem(item: VaultItem): void {
    this.selectedItem = item;
    this.showHistory = null;
    this.renderContent();
    this.renderDetail();
  }

  private renderDetail(): void {
    const detailSection = document.getElementById('vault-detail-section');
    const detailEl = document.getElementById('vault-detail');

    if (!detailSection || !detailEl) return;

    if (!this.selectedItem) {
      detailSection.classList.add('hidden');
      return;
    }

    detailSection.classList.remove('hidden');

    detailEl.innerHTML = `
      <div class="vault-detail-header">
        <h2>${this.escapeHtml(this.selectedItem.name)}</h2>
        <div class="vault-detail-actions">
          <button class="vault-detail-action" id="vault-edit" title="Edit">
            <span class="material-symbols-outlined">edit</span>
          </button>
          <button class="vault-detail-action" id="vault-delete" title="Delete">
            <span class="material-symbols-outlined">delete</span>
          </button>
        </div>
      </div>
      ${this.selectedItem.notes ? `
        <div class="vault-detail-notes">${this.escapeHtml(this.selectedItem.notes)}</div>
      ` : ''}
      <div class="vault-detail-values">
        ${this.selectedItem.values.map(value => this.renderValue(value)).join('')}
      </div>
      <div class="vault-detail-meta">
        <span>Created: ${this.formatDate(this.selectedItem.createdAt)}</span>
        <span>Updated: ${this.formatDate(this.selectedItem.updatedAt)}</span>
      </div>
    `;

    // Edit button
    document.getElementById('vault-edit')?.addEventListener('click', () => {
      if (this.selectedItem) {
        this.openEditModal(this.selectedItem);
      }
    });

    // Delete button
    document.getElementById('vault-delete')?.addEventListener('click', async () => {
      if (this.selectedItem && confirm(`Delete "${this.selectedItem.name}"?`)) {
        try {
          await vaultApi.delete(this.selectedItem.id);
          this.selectedItem = null;
          await this.loadData();
          this.renderDetail();
        } catch (error) {
          console.error('Failed to delete item:', error);
        }
      }
    });

    // Copy buttons
    detailEl.querySelectorAll('.vault-value-copy').forEach(btn => {
      btn.addEventListener('click', async () => {
        const value = btn.getAttribute('data-value') || '';
        await navigator.clipboard.writeText(value);
        const icon = btn.querySelector('.material-symbols-outlined');
        if (icon) {
          icon.textContent = 'check';
          setTimeout(() => { icon.textContent = 'content_copy'; }, 1500);
        }
      });
    });

    // History toggle buttons
    detailEl.querySelectorAll('.vault-value-history-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.getAttribute('data-key') || '';
        if (this.showHistory?.itemId === this.selectedItem?.id && this.showHistory?.valueKey === key) {
          this.showHistory = null;
        } else {
          this.showHistory = { itemId: this.selectedItem!.id, valueKey: key };
        }
        this.renderDetail();
      });
    });
  }

  private renderValue(value: VaultValue): string {
    const isShowingHistory = this.showHistory?.itemId === this.selectedItem?.id && this.showHistory?.valueKey === value.key;
    const hasHistory = value.history.length > 0;

    return `
      <div class="vault-value">
        <div class="vault-value-header">
          <span class="vault-value-key">${this.escapeHtml(value.key)}</span>
          <div class="vault-value-actions">
            ${hasHistory ? `
              <button class="vault-value-history-btn ${isShowingHistory ? 'active' : ''}" data-key="${this.escapeHtml(value.key)}" title="Version History (${value.history.length})">
                <span class="material-symbols-outlined">history</span>
                <span class="vault-history-count">${value.history.length}</span>
              </button>
            ` : ''}
            <button class="vault-value-copy" data-value="${this.escapeHtml(value.value)}" title="Copy">
              <span class="material-symbols-outlined">content_copy</span>
            </button>
          </div>
        </div>
        <div class="vault-value-content">
          <code class="vault-value-text">${this.escapeHtml(value.value)}</code>
        </div>
        ${isShowingHistory ? `
          <div class="vault-value-history">
            <div class="vault-history-title">Previous Values</div>
            ${value.history.map((h, i) => `
              <div class="vault-history-item">
                <span class="vault-history-date">${this.formatDate(h.date)}</span>
                <code class="vault-history-value">${this.escapeHtml(h.value)}</code>
                <button class="vault-value-copy" data-value="${this.escapeHtml(h.value)}" title="Copy">
                  <span class="material-symbols-outlined">content_copy</span>
                </button>
              </div>
            `).join('')}
          </div>
        ` : ''}
      </div>
    `;
  }

  private openCreateModal(): void {
    this.editingItem = null;
    const modal = document.getElementById('vault-modal');
    const title = document.getElementById('vault-modal-title');
    const body = document.getElementById('vault-modal-body');

    if (!modal || !body || !title) return;

    title.textContent = 'Add Vault Item';
    body.innerHTML = this.renderModalForm(null);
    modal.classList.remove('hidden');

    this.attachModalFormListeners();
    (document.getElementById('vault-form-name') as HTMLInputElement)?.focus();
  }

  private openEditModal(item: VaultItem): void {
    this.editingItem = item;
    const modal = document.getElementById('vault-modal');
    const title = document.getElementById('vault-modal-title');
    const body = document.getElementById('vault-modal-body');

    if (!modal || !body || !title) return;

    title.textContent = 'Edit Vault Item';
    body.innerHTML = this.renderModalForm(item);
    modal.classList.remove('hidden');

    this.attachModalFormListeners();
  }

  private renderModalForm(item: VaultItem | null): string {
    const values = item?.values || [{ key: 'API Key', value: '', history: [] }];

    return `
      <div class="vault-form">
        <div class="vault-form-group">
          <label for="vault-form-name">Name</label>
          <input type="text" id="vault-form-name" class="vault-form-input" value="${this.escapeHtml(item?.name || '')}" placeholder="e.g., OpenAI API Key">
        </div>
        <div class="vault-form-group">
          <label>Values</label>
          <div id="vault-form-values">
            ${values.map((v, i) => `
              <div class="vault-form-value-row" data-index="${i}">
                <input type="text" class="vault-form-input vault-form-key" placeholder="Key name" value="${this.escapeHtml(v.key)}">
                <input type="text" class="vault-form-input vault-form-value" placeholder="Value" value="${this.escapeHtml(v.value)}">
                <button type="button" class="vault-form-remove-value" title="Remove">
                  <span class="material-symbols-outlined">remove</span>
                </button>
              </div>
            `).join('')}
          </div>
          <button type="button" class="vault-form-add-value" id="vault-add-value">
            <span class="material-symbols-outlined">add</span> Add Value
          </button>
        </div>
        <div class="vault-form-group">
          <label for="vault-form-notes">Notes (optional)</label>
          <textarea id="vault-form-notes" class="vault-form-textarea" rows="3" placeholder="Notes about this item...">${this.escapeHtml(item?.notes || '')}</textarea>
        </div>
      </div>
    `;
  }

  private attachModalFormListeners(): void {
    // Add value button
    document.getElementById('vault-add-value')?.addEventListener('click', () => {
      const container = document.getElementById('vault-form-values');
      if (!container) return;

      const index = container.children.length;
      const row = document.createElement('div');
      row.className = 'vault-form-value-row';
      row.setAttribute('data-index', String(index));
      row.innerHTML = `
        <input type="text" class="vault-form-input vault-form-key" placeholder="Key name" value="">
        <input type="text" class="vault-form-input vault-form-value" placeholder="Value" value="">
        <button type="button" class="vault-form-remove-value" title="Remove">
          <span class="material-symbols-outlined">remove</span>
        </button>
      `;
      container.appendChild(row);
      this.attachRemoveValueListeners();
      (row.querySelector('.vault-form-key') as HTMLInputElement)?.focus();
    });

    this.attachRemoveValueListeners();
  }

  private attachRemoveValueListeners(): void {
    document.querySelectorAll('.vault-form-remove-value').forEach(btn => {
      btn.replaceWith(btn.cloneNode(true));
    });
    document.querySelectorAll('.vault-form-remove-value').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const row = (e.target as HTMLElement).closest('.vault-form-value-row');
        if (row && document.querySelectorAll('.vault-form-value-row').length > 1) {
          row.remove();
        }
      });
    });
  }

  private async saveItem(): Promise<void> {
    const nameInput = document.getElementById('vault-form-name') as HTMLInputElement;
    const notesInput = document.getElementById('vault-form-notes') as HTMLTextAreaElement;
    const valueRows = document.querySelectorAll('.vault-form-value-row');

    const name = nameInput?.value.trim();
    if (!name) {
      nameInput?.focus();
      return;
    }

    const values: Array<{ key: string; value: string }> = [];
    valueRows.forEach(row => {
      const key = (row.querySelector('.vault-form-key') as HTMLInputElement)?.value.trim();
      const value = (row.querySelector('.vault-form-value') as HTMLInputElement)?.value.trim();
      if (key && value) {
        values.push({ key, value });
      }
    });

    if (values.length === 0) {
      alert('At least one key-value pair is required');
      return;
    }

    try {
      if (this.editingItem) {
        // Update existing
        const updated = await vaultApi.update(this.editingItem.id, {
          name,
          values,
          notes: notesInput?.value.trim() || undefined
        });
        this.selectedItem = updated;
      } else {
        // Create new
        const created = await vaultApi.create({
          name,
          values,
          notes: notesInput?.value.trim() || undefined
        });
        this.selectedItem = created;
      }

      this.closeModal();
      await this.loadData();
      this.renderDetail();
    } catch (error) {
      console.error('Failed to save item:', error);
      alert('Failed to save item');
    }
  }

  private closeModal(): void {
    document.getElementById('vault-modal')?.classList.add('hidden');
    this.editingItem = null;
  }

  private formatDate(timestamp: number): string {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}
